import Vue from 'vue'
import Router from 'vue-router'

import {Session} from '@/common/Session'
import store from '@/store'
import UserService from '@/common/UserService'

Vue.use(Router)

const router = new Router({
  mode: 'history',
  routes: [
    {
      path: '/',
      name: 'home',
      component: () => import('@/components/Welcome'),
      meta: {requiresAuth: true}
    },
    {
      path: '/login',
      name: 'Login',
      component: () => import('@/components/Login'),
      meta: {requiresAuth: false}
    },
    {
      path: '/code',
      name: 'Code',
      component: () => import('@/components/Code'),
      meta: {requiresAuth: false, roles: ['ROLE_PRE_AUTH_USER']}
    },
    {
      path: '/forgot-password',
      name: 'ForgotPassword',
      component: () => import('@/components/ForgotPassword'),
      meta: {requiresAuth: false}
    },
    {
      path: '/reset-password',
      name: 'ResetPassword',
      component: () => import('@/components/ResetPassword'),
      meta: {requiresAuth: false}
    },
    {
      path: '/users',
      name: 'users',
      component: () => import('@/components/Users'),
      meta: {requiresAuth: true, roles: ['ROLE_ADMIN']}
    },
    {
      path: '/verify',
      name: 'verify',
      component: () => import('@/components/VerifyUser'),
      props: true,
      meta: {requiresAuth: false},
    },
    {
      path: '/users/me',
      name: 'AccountSettings',
      component: () => import('@/components/AccountSettings'),
      meta: {requiresAuth: true}
    },
    {
      path: '/users/me/change-password',
      name: 'ChangePassword',
      component: () => import('@/components/PasswordChange'),
      meta: {requiresAuth: true}
    },
    {
      path: '/users/me/twofactor',
      name: 'TwoFactorAuthSettings',
      component: () => import('@/components/TwoFactorAuthSettings'),
      meta: {requiresAuth: true}
    },
    {
      path: '/benchmarking-objects',
      name: 'benchmarkingObjects',
      component: () => import('@/components/benchmarkingObjects/BenchmarkingObjects'),
      meta: {requiresAuth: true, roles: ['ROLE_ADMIN']}
    },
    {
      path: '/benchmarking-objects/:benchmarkingObjectId',
      name: 'benchmarkingObjectEdit',
      component: () => import('@/components/benchmarkingObjects/BmBenchmarkingObjectEdit'),
      meta: {requiresAuth: true},
    },
    {
      path: '/benchmarking-groups',
      name: 'benchmarkingGroups',
      component: () => import('@/components/benchmarkingGroups/BenchmarkingGroups'),
      meta: {requiresAuth: true, roles: ['ROLE_ADMIN']}
    },
    {
      path: '/benchmarking-groups/:benchmarkingGroupId',
      name: 'benchmarkingGroupEdit',
      component: () => import('@/components/benchmarkingGroups/BmBenchmarkingGroupEdit'),
      meta: {requiresAuth: true},
    },

    {
      path: '/agenda-topics',
      name: 'agendaTopics',
      component: () => import('@/components/agendaTopics/BmAgendaTopics'),
      meta: {requiresAuth: true, roles: ['ROLE_ADMIN']}
    },

    {
      path: '/agenda-topics/:agendaTopicId/agenda-sub-topics',
      name: 'agendaSubTopics',
      component: () => import('@/components/agendaTopics/BmAgendaSubTopics'),
      meta: {requiresAuth: true},
    },
    {
      path: '/datenpool/explorer',
      name: 'datenpoolExplorer',
      component: () => import('@/components/datenpool/DpFileExplorer'),
      meta: {requiresAuth: true}
    },
    {
      path: '/datenpool/accesses',
      name: 'datenpoolAccesses',
      component: () => import('@/components/datenpool/accessLog/DpFileAccessLogDataTable'),
      meta: {requiresAuth: true}
    },
    {
      path: '/datenpool/tags',
      name: 'datenpoolTags',
      component: () => import('@/components/datenpool/tags/DpTagsDataTable'),
      meta: {requiresAuth: true, roles: ['ROLE_ADMIN']}
    },
    {
      path: '/datenpool/categories',
      name: 'datenpoolCategories',
      component: () => import('@/components/datenpool/categories/DpCategoriesDataTable'),
      meta: {requiresAuth: true, roles: ['ROLE_ADMIN']}
    },

    {
      path: '/workshops',
      name: 'workshops',
      component: () => import('@/components/workshops/admin/Workshops'),
      meta: {requiresAuth: true, roles: ['ROLE_ADMIN']}
    },
    {
      path: '/workshops/:workshopId',
      name: 'workshop',
      component: () => import('@/components/workshops/admin/BmWorkshop'),
      meta: {requiresAuth: true},
      children: [
        {
          path: 'edit',
          name: 'workshopEdit',
          component: () => import('@/components/workshops/admin/BmEditWorkshop'),
          meta: {requiresAuth: true, roles: ['ROLE_ADMIN']},
        },

        {
          path: 'attendees',
          name: 'attendees',
          component: () => import('@/components/workshops/admin/BmWorkshopAttendees'),
          meta: {requiresAuth: true, roles: ['ROLE_ADMIN']},
        },

        {
          path: 'modules/:workshopModuleId/kpi-recordings',
          name: 'workshopModuleKpiRecordings',
          component: () => import('@/components/workshops/admin/recordings/WorkshopModuleKpiRecordings'),
          meta: {requiresAuth: true},
        },

        {
          path: 'modules/:workshopModuleId/plannings',
          name: 'workshopModuleKpiPlannings',
          component: () => import('@/components/workshops/admin/recordings/WorkshopModulePlannings'),
          meta: {requiresAuth: true},
        },
        {
          path: 'modules/:workshopModuleId/prepare-status-charts',
          name: 'workshopModulePrepareStatusCharts',
          component: () => import('@/components/workshops/admin/chartsPrepare/BmWorkshopModulePrepareStatusCharts'),
          meta: {requiresAuth: true},
        },
        {
          path: 'modules/:workshopModuleId/prepare-planning-charts',
          name: 'workshopModulePreparePlanningCharts',
          component: () => import('@/components/workshops/admin/chartsPrepare/BmWorkshopModulePreparePlanningCharts'),
          meta: {requiresAuth: true},
        },
        {
          path: 'modules/:workshopModuleId/status-sheet',
          name: 'workshopModulePrepareStatusResultSheet',
          component: () => import('@/components/workshops/resultSheets/BmWorkshopModuleStatusResultSheet'),
          meta: {requiresAuth: true, roles: ['ROLE_ADMIN']},
        },
        {
          path: 'prepare-agenda',
          name: 'workshopPrepareAgenda',
          component: () => import('@/components/workshops/admin/agenda/BmWorkshopEditAgenda'),
          meta: {requiresAuth: true},
        },
        {
          path: 'prepare-topics',
          name: 'workshopPrepareTopics',
          component: () => import('@/components/workshops/admin/topics/BmWorkshopEditTopics'),
          meta: {requiresAuth: true},
        },
        {
          path: 'prepare-maßnahmen',
          name: 'workshopPrepareMaßnahmen',
          component: () => import('@/components/workshops/admin/maßnahmen/BmWorkshopEditMaßnahmen'),
          meta: {requiresAuth: true},
        },
        {
          path: 'datenpool',
          name: 'workshopDatenpool',
          component: () => import('@/components/workshops/admin/datenpool/BmWorkshopEditDatenpool'),
          meta: {requiresAuth: true},
        },
      ]
    },

    {
      path: '/my-workshops',
      name: 'myWorkshops',
      component: () => import('@/components/workshops/user/MyWorkshops'),
      meta: {requiresAuth: true}
    },

    {
      path: '/teilnehmerliste',
      name: 'teilnehmerliste',
      component: () => import('@/components/workshops/user/teilnehmerliste/BmTeilnehmerliste'),
      meta: {requiresAuth: true}
    },

    {
      path: '/maßnahmenUebersicht',
      name: 'maßnahmenUebersicht',
      component: () => import('@/components/workshops/user/maßnahmen/BmMaßnahmenUebersicht'),
      meta: {requiresAuth: true}
    },

    {
      path: '/workshops/:workshopId/agenda',
      name: 'workshopAgenda',
      component: () => import('@/components/workshops/user/BmWorkshopBmoAgenda'),
      meta: {requiresAuth: true},
    },

    {
      path: '/workshops/:workshopId/maßnahmen',
      name: 'workshopMaßnahmen',
      component: () => import('@/components/workshops/user/maßnahmen/BmWorkshopMaßnahmen'),
      meta: {requiresAuth: true},
    },
    {
      path: '/workshops/:workshopId/datenpoolUser',
      name: 'workshopDatenpoolUser',
      component: () => import('@/components/workshops/user/BmWorkshopDatenpool'),
      meta: {requiresAuth: true},
    },

    {
      path: '/workshops/:workshopId/modules/',
      name: 'workshopModules',
      component: () => import('@/components/workshops/user/BmWorkshopModules'),
      meta: {requiresAuth: true},
      children: [
        {
          path: '/workshops/:workshopId/modules/:workshopModuleId/charts',
          name: 'workshopModuleCharts',
          component: () => import('@/components/workshops/BmWorkshopModuleCharts'),
          meta: {requiresAuth: true},
        },
        {
          path: '/workshops/:workshopId/modules/:workshopModuleId/result-sheet',
          name: 'workshopModuleSheet',
          component: () => import('@/components/workshops/resultSheets/BmWorkshopModuleStatusResultSheet'),
          meta: {requiresAuth: true},
        },
      ]
    },

    {
      path: '/workshops/:workshopId/recording-modules/',
      name: 'workshopRecordingModules',
      component: () => import('@/components/workshops/user/BmWorkshopRecordingModules'),
      meta: {requiresAuth: true},
      children: [
        {
          path: '/workshops/:workshopId/modules/:workshopModuleId/recordings',
          name: 'workshopModuleRecordings',
          component: () => import('@/components/workshops/user/BmWorkshopRecordingEdit'),
          meta: {requiresAuth: true},
        },
      ]
    },

    {
      path: '/development-chart/analysisType/:analysisTypeId/benchmarkingObject/:benchmarkingObjectId',
      name: 'developmentChart',
      component: () => import('@/components/development/admin/DevelopmentChart'),
      meta: {requiresAuth: true, roles: ['ROLE_ADMIN']}
    },
    {
      path: '/development-chart-config/benchmarkingObject/:benchmarkingObjectId',
      name: 'developmentChartConfig',
      component: () => import('@/components/development/admin/DevelopmentChartConfig'),
      meta: {requiresAuth: true, roles: ['ROLE_ADMIN']}
    },
    {
      path: '/bm-development',
      name: 'bmDevelopment',
      component: () => import('@/components/development/admin/BmDevelopment'),
      meta: {requiresAuth: true, roles: ['ROLE_ADMIN']}
    },
    {
      path: '/my-bm-development',
      name: 'myBmDevelopment',
      component: () => import('@/components/development/user/MyBmDevelopment'),
      meta: {requiresAuth: true}
    },

    {
      path: '/my-development-chart',
      name: 'myDevelopmentChart',
      component: () => import('@/components/development/user/MyDevelopmentChart'),
      meta: {requiresAuth: true}
    },

    {
      path: '/analysis-types',
      name: 'analysisTypes',
      component: () => import('@/components/analysisTypes/BmAnalysisTypes'),
      meta: {requiresAuth: true, roles: ['ROLE_ADMIN']}
    },

    {
      path: '/analysis-types/:analysisTypeId',
      name: 'analysisType',
      component: () => import('@/components/analysisTypes/BmAnalysisType'),
      meta: {requiresAuth: true},
      children: [
        {
          path: 'edit',
          name: 'analysisTypesBaseDataEdit',
          component: () => import('@/components/analysisTypes/BmAnalysisTypeBaseDataEdit'),
          meta: {requiresAuth: true, roles: ['ROLE_ADMIN']},
        },
        {
          path: 'kpis',
          name: 'analysisTypesKpis',
          component: () => import('@/components/analysisTypes/kpis/BmAnalysisTypeKpis'),
          meta: {requiresAuth: true, roles: ['ROLE_ADMIN']},
        },
        {
          path: 'input-forms',
          name: 'analysisTypesInputForms',
          component: () => import('@/components/analysisTypes/inputForms/BmAnalysisTypeInputForms'),
          meta: {requiresAuth: true, roles: ['ROLE_ADMIN']},
        },
        {
          path: 'status-charts',
          name: 'analysisTypesStatusCharts',
          component: () => import('@/components/analysisTypes/charts/BmAnalysisTypeStatusCharts'),
          meta: {requiresAuth: true, roles: ['ROLE_ADMIN']},
        },
        {
          path: 'planning-charts',
          name: 'analysisTypesPlanningCharts',
          component: () => import('@/components/analysisTypes/charts/BmAnalysisTypePlanningCharts'),
          meta: {requiresAuth: true, roles: ['ROLE_ADMIN']},
        },
        {
          path: 'development-groups',
          name: 'analysisTypesDevelopmentGroups',
          component: () => import('@/components/analysisTypes/developmentGroup/BmAnalysisTypeDevelopmentGroup'),
          meta: {requiresAuth: true, roles: ['ROLE_ADMIN']},
        }
      ]
    },

    {
      path: '/analysis-types/:analysisTypeId/kpis/:kpiId',
      name: 'analysisTypesKpiEdit',
      component: () => import('@/components/analysisTypes/kpis/BmAnalysisTypesKpiEdit'),
      meta: {requiresAuth: true},
    },

    {
      path: '/analysis-types/:analysisTypeId/input-forms/:inputFormId',
      name: 'analysisTypesInputFormEdit',
      component: () => import('@/components/analysisTypes/inputForms/BmAnalysisTypesInputFormEdit'),
      meta: {requiresAuth: true},
    },

    {
      path: '/analysis-types/:analysisTypeId/development-groups/:developmentGroupId',
      name: 'analysisTypesDevelopmentGroupEdit',
      component: () => import('@/components/analysisTypes/developmentGroup/BmAnalysisTypeDevelopmentGroupEdit'),
      meta: {requiresAuth: true},
    },

    {
      path: '/record-years',
      name: 'recordYears',
      component: () => import('@/components/recordYears/RecordYears'),
      meta: {requiresAuth: true, roles: ['ROLE_ADMIN']}
    },
    {
      path: '/record-years/:recordYearId',
      name: 'recordYearEdit',
      component: () => import('@/components/recordYears/BmRecordYearEdit'),
      meta: {requiresAuth: true},
    },

    {
      path: '/record-years/:recordYearId/record-year-plannings/:benchmarkingGroupId',
      name: 'benchmarkingGroupRecordYearPlannings',
      component: () => import('@/components/recordYears/BenchmarkingGroupRecordYearPlannings'),
      meta: {requiresAuth: true},
    },

    {
      path: '/analysis-types/:analysisTypeId/my',
      name: 'myAnalysisType',
      component: () => import('@/components/objectKpiRecordings/MyAnalysisType'),
      meta: {requiresAuth: true},
      children: [
        {
          path: 'recordings',
          name: 'myAnalysisTypeRecordings',
          component: () => import('@/components/objectKpiRecordings/MyAnalysisTypeRecordings'),
          meta: {requiresAuth: true},
        },
        {
          path: 'plannings',
          name: 'myAnalysisTypePlannings',
          component: () => import('@/components/objectRecordYearPlannings/MyAnalysisTypePlannings'),
          meta: {requiresAuth: true},
        },
      ]
    },
    {
      path: '/analysis-types/:analysisTypeId/admin',
      name: 'adminAnalysisType',
      component: () => import('@/components/objectKpiRecordings/AdminAnalysisType'),
      meta: {requiresAuth: true},
      children: [
        {
          path: 'recordings',
          name: 'adminAnalysisTypeRecordings',
          component: () => import('@/components/objectKpiRecordings/AdminAnalysisTypeRecordings'),
          meta: {requiresAuth: true},
        },
        {
          path: 'plannings',
          name: 'adminAnalysisTypePlannings',
          component: () => import('@/components/objectRecordYearPlannings/AdminAnalysisTypePlannings'),
          meta: {requiresAuth: true},
        },
      ]
    },
    {
      path: '/analysis-types/:analysisTypeId/recordings/:benchmarkingObjectId/:objectKpiRecordingId',
      name: 'kpiRecordingEdit',
      component: () => import('@/components/objectKpiRecordings/BmObjectKpiRecordingEdit'),
      meta: {requiresAuth: true},
    },
    {
      path: '/analysis-types/:analysisTypeId/plannings/:benchmarkingObjectId/:objectRecordYearPlanningId',
      name: 'recordYearPlanningEdit',
      component: () => import('@/components/objectRecordYearPlannings/BmObjectRecordYearPlaningEdit'),
      meta: {requiresAuth: true},
    },
  ]
})

router.beforeEach((to, from, next) => {
  // always wait for possible session restore
  Session.getSession().then(() => {
    if (to.matched.some(record => record.meta.requiresAuth)) {
      // Diese Route benötigt Authentifizierung und prüft,
      // ob man eingeloggt ist.
      // Wenn nicht, Redirect zur Login-Seite.

      if (store.state.Session.twoFactorEmailCode) {
        next({
          path: '/code',
        })
      } else {
        if (store.state.Session.authenticated) {
          if (!to.meta.roles || UserService.hasAnyRole(to.meta.roles)) {
            next()
          }
        } else {
          next({
            path: '/login',
            query: {redirect: to.fullPath}
          })
        }
      }
    } else {
      next() // Rufe immer next() auf.
    }
  }).catch(() => {
    // if session loading failed we redirect to login
    next({
      path: '/login',
      query: {redirect: to.fullPath}
    })
  })
})

export default router



// WEBPACK FOOTER //
// ./src/router/index.js